/* eslint-disable no-throw-literal */
import React from "react";
import {
  CustomTable,
  useDropdownApis,
  useAppConfig,
} from "../../../shared";
import {
  Button,
  Checkbox,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { useImmer } from "use-immer";
import InputControl from "../../../shared/components/InputControl";
import { errorMessage, successMessage } from "../../../utils";
import {
  getClientAccountRequestsApi,
  sendClientAccountCreateInviteApi,
} from "../api/clientAccountApi";
import { useLocation } from "react-router-dom";
import { SendEmailsDialog } from "./SendEmailsDialog";
import { TableStateContext } from "../../../store";

export const ClientAccountRequestList = () => {
  const location = useLocation();
  const isStoreAdmin = location.pathname.includes("/store-admin");
  const { taxIdTerm } = useAppConfig();
  const {
    state,
    storeList,
    triggerDialog,
    handleSendInvite,
    handleClearFilter,
    handleApplyFilter,
    handleFilterChange,
    handleClearAllFilter,
  } = useManageRequestsList();

  const columns = React.useMemo(
    () => [
      { Header: "Client Account Name", accessor: "account_name" },
      { Header: taxIdTerm, accessor: "abn_number" },
      { Header: "Store", accessor: "store_name" },
      {
        Header: "Address",
        accessor: "default_addresses",
        Cell: ({
          row: {
            original: { default_addresses },
          },
        }) => {
          return (
            <Typography fontSize={14}>
              {default_addresses?.address ? default_addresses.address : "-"}
            </Typography>
          );
        },
      },
      {
        Header: "Action",
        id: "action",
        align: "right",
        disableSortBy: true,
        Cell: () => <ArrowForward />,
      },
    ],
    [taxIdTerm]
  );

  const tableFilter = {
    filterApplied: state.filter.appliedFilter,
    filterSection: (
      <Stack flexDirection="row" justifyContent="space-between" width="100%">
        <InputControl
          type="dropdown"
          name="store"
          label="Store"
          multiple={true}
          options={storeList}
          disableCloseOnSelect
          disableClearable={true}
          value={state.filter.selectedFilter}
          onChange={(_, value) => handleFilterChange(value)}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderTags={() => <></>}
          renderOption={(props, option, { selected }) => (
            <ListItem {...props} key={option.id}>
              <Checkbox style={{ marginRight: 8 }} checked={selected} />
              {option.name}
            </ListItem>
          )}
          sx={{ width: "250px" }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Store"
              required
              placeholder="Select Store"
            />
          )}
        />
        <Button
          variant="contained"
          onClick={handleApplyFilter}
          disabled={state.isFilterApplied}
        >
          Apply
        </Button>
      </Stack>
    ),
    onAllFilterClear: handleClearAllFilter,
    onFilterClear: handleClearFilter,
  };

  const headerRightContent = {
    headerRightContent: (
      <Button onClick={() => triggerDialog(true)}>Send Invite</Button>
    ),
  };

  return (
    <React.Fragment>
      <CustomTable
        tableTitle={isStoreAdmin ? "Requests" : undefined}
        columns={columns}
        data={state.requests.list}
        isLoading={state.requests.loading}
        {...(isStoreAdmin ? headerRightContent : tableFilter)}
        onRowLink={(row) => `request/${row.id}`}
        tableId="ca-manage-requests"
      />
      <SendEmailsDialog
        open={state.isDialogOpen}
        key={state.isDialogOpen}
        disableCloseOnBackgroundClick
        handleClose={() => triggerDialog(false)}
        handleFormSubmit={handleSendInvite}
        title="Client Account Invitation"
        helperText="Please enter email address to send invite."
        bottomHelperText="Please note that this link will expire after 3 days."
        submitting={state.inviteButtonLoading}
      />
    </React.Fragment>
  );
};

const useManageRequestsList = () => {
  const tableName = "manage-requests";
  const defaultFilter = {
    appliedFilter: [],
    selectedFilter: [],
  };

  const { filterState, filterStateDispatch } =
    React.useContext(TableStateContext);

  const storedFilter = filterState.params[tableName];

  const [state, setState] = useImmer({
    requests: { list: [], loading: true },
    filter: storedFilter ? storedFilter : defaultFilter,
    isFilterApplied: true,
    isDialogOpen: false,
    inviteButtonLoading: false,
  });

  const { storeList } = useDropdownApis({ isStoreList: true });

  // ============================================================ API SECTION ====================================================
  const getClientAccountRequests = async ({
    // searchText = "",
    filters = null,
  }) => {
    triggerTableLoading(true);
    let params = {};
    try {
      if (filters) {
        const appliedFilters = await getStoreFilterParams({
          appliedFilters: filters,
        });
        params = { ...appliedFilters };
      }

      // if (searchText) {
      //   params.search = searchText;
      // }
      const response = await getClientAccountRequestsApi(params);
      const { success, data, message } = response;

      if (success) {
        filterStateDispatch({
          type: "SET_DATA",
          page: tableName,
          data: state.filter,
        });

        setState((draft) => {
          draft.requests.list = data;
          draft.isFilterApplied = true;
        });
      } else {
        throw {
          response: {
            data: { message },
          },
        };
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        "Field to fetch client account requests";

      errorMessage(message);
    } finally {
      triggerTableLoading(false);
    }
  };

  const sendClientInvite = async (emails) => {
    triggerInviteButtonLoading(true);
    try {
      const response = await sendClientAccountCreateInviteApi({ emails });
      const { success, message } = response;
      if (success) {
        successMessage(message);
        triggerDialog(false);
      } else {
        throw { response: { data: { message } } };
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ?? "Failed to send the invite";

      errorMessage(message);
    } finally {
      triggerInviteButtonLoading(false);
    }
  };

  // ===================================================== UTIL FUNCTION SECTION =================================================
  const triggerTableLoading = (loading) => {
    setState((draft) => {
      draft.requests.loading = loading;
    });
  };

  const triggerInviteButtonLoading = (loading) => {
    setState((draft) => {
      draft.inviteButtonLoading = loading;
    });
  };

  const getStoreFilterParams = async ({ appliedFilters = [] }) => {
    const params = {};
    params["store_id"] = appliedFilters.map((filter) => filter?.id);
    return params;
  };

  const handleFilterChange = (value) => {
    setState((draft) => {
      draft.isFilterApplied = false;
      draft.filter.selectedFilter = value;
      draft.filter.appliedFilter = value.map((filter) => ({
        label: filter.name,
        type: "Store",
        ...filter,
      }));
    });
  };

  const handleClearFilter = (value) => {
    const updatedFilter = state.filter.selectedFilter.filter(
      (appliedFilter) => appliedFilter.id !== value.id
    );

    handleFilterChange(updatedFilter);
  };

  const handleClearAllFilter = async () => {
    handleFilterChange([]);
    await getClientAccountRequests({});
  };

  const handleApplyFilter = async () => {
    console.log(state.filter.appliedFilter);
    await getClientAccountRequests({ filters: state.filter.appliedFilter });
  };

  const triggerDialog = (open) =>
    setState((draft) => {
      draft.isDialogOpen = open;
    });

  const handleSendInvite = async (emails) => {
    await sendClientInvite(emails);
  };

  // ========================================================== USE EFFECT SECTION ===============================================
  React.useEffect(() => {
    getClientAccountRequests({ filters: state.filter.appliedFilter });
  }, []);

  return {
    state,
    storeList,
    triggerDialog,
    handleSendInvite,
    handleClearFilter,
    handleApplyFilter,
    handleFilterChange,
    handleClearAllFilter,
  };
};
